import React, { useRef, useState, useEffect } from 'react';
import { Box, makeStyles, Typography, Card, CardContent } from '@material-ui/core';
import { styleBreakMobile, styleBreakDesktop } from '../utilities/layouts/Globals';
import { ContentLayout } from '../utilities/layouts/ContentLayout';
import { CardItem } from '../utilities/commonUtilities/CardItem';
import NextUp from '../utilities/commonUtilities/NextUpComponent';
import { WAITING_REVIEW_NEXT_UP_CARD_TEXT } from '../../resources/messages.json';
import ChatModal from '../../resources/images/closed-chat-modal.svg';
import { ReactComponent as BloodBadge } from '../../resources/images/blood-badge.svg';
import { ReactComponent as NoteIcon } from '../../resources/images/note.svg';
import TutorialFloatingGrid from './TutorialFloatingGrid';
import { CalendarProgressMobile } from '../calendar/CalendarProgress';
import clsx from 'clsx';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
    tutorialContent: {
        marginBottom: theme.spacing(12),
        [styleBreakDesktop(theme)]: {
            marginTop: theme.spacing(12),
            paddingLeft: theme.spacing(6),
            paddingRight: theme.spacing(6),
        },
    },
    subPageContent: {
        [styleBreakMobile(theme)]: {
            marginTop: theme.spacing(2),
        },
        [styleBreakDesktop(theme)]: {
            marginTop: theme.spacing(15),
        },
    },
    chatModal: {
        position: 'absolute',
        right: 0,
        bottom: theme.spacing(6.5),
    },
    yourJourneycard: {
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.25)',
        borderRadius: '5px',
        marginTop: theme.spacing(1.5),
        marginBottom: theme.spacing(3),
    },
    cardContent: {
        padding: theme.spacing(3),
    },
    stageLabel: {
        marginBottom: theme.spacing(1.5),
        fontWeight: 600,
    },
    timelineProgressBar: {
        position: 'relative',
        width: '100%',
    },
    header: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
        letterSpacing: theme.spacing(0.03),
        fontSize: theme.spacing(2.5),
        [styleBreakMobile(theme)]: {
            paddingTop: theme.spacing(2),
        },
    },
    bloodTestCardContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    bloodTestDate: {
        fontWeight: 500,
        marginBottom: theme.spacing(2),
        letterSpacing: theme.spacing(0.02),
        fontSize: theme.spacing(2) - 2,
        paddingTop: theme.spacing(1),
    },
    bloodTestDaysLeft: {
        background: theme.palette.primary.light,
        borderRadius: theme.spacing(0.5) - 1,
        padding: `${theme.spacing(1) - 1.5}px ${theme.spacing(1)}px`,
    },
}));

export default function OnboardingTutorial({ tutorialCurrentPage, isMobile, type }) {
    const classes = useStyles();
    const customRef = useRef(null);
    const [x, setX] = useState();
    let SubPageContent = null;

    const multiPageInfo = {
        2: {
            title: 'Quick Access Menu',
            body: 'You can quickly access any page from anywhere inside the app by tapping this icon.',
            lPadding: 'calc(100% - 494px)',
        },
        3: {
            title: 'Clinician Chat',
            body: 'You can reach out to your clinician any time you have a question by tapping on this icon.',
        },
        4: {
            title: 'Your To-Do List',
            body: 'Think of this as your To-Do list, everything you need to complete in a certain time frame all in one place.',
        },
        5: {
            title: "See What's Coming",
            body: 'There will always be a card showing you what to expect next in your journey, so there are no surprises.',
        },
        6: {
            title: 'See Where You Are',
            body: 'We sometimes get lost in our day-to-day life that its easy to loose track of where we are, here you can get a quick overview and even access a larger calendar',
        },
        7: {
            title: 'Tutorials & FAQs',
            body: 'Re-visit this tutorial and other FAQs at any time through the “Settings” tab.',
            lPadding: 'calc(100% - 494px)',
        },
    };

    // This function calculate X-Cordinate
    const getPosition = () => {
        const x = customRef?.current?.offsetLeft;
        setX(x);
    };

    useEffect(() => {
        getPosition();
        window.addEventListener('resize', getPosition);
        return () => {
            window.removeEventListener('resize', getPosition);
        };
    });

    const BloodTestCard = () => {
        return (
            <>
                <Typography className={classes.header}>Your Tasks</Typography>
                <Box className={classes.bloodTestCardContainer}>
                    <Typography className={classes.bloodTestDate}>Due October 26, 2020</Typography>
                    <Typography className={clsx(classes.bloodTestDate, classes.bloodTestDaysLeft)}>10 days left</Typography>
                </Box>
                <CardItem title={'Get a Blood Test'} Badge={BloodBadge} subtitle={'Click to begin'} noHoverEffect />
            </>
        );
    };

    const JourneyCard = () => {
        const dateOfDiagnosis = moment('2021-12-05');
        const endOfTreatementDate = isMobile ? moment('2030-12-05') : moment('2026-12-05');
        const seminomaLabel = 'Stage 1 Non-Seminoma';
        return (
            <>
                <Typography className={classes.header}>Your Journey</Typography>
                <Card className={classes.yourJourneycard}>
                    <CardContent className={classes.cardContent}>
                        <Typography variant="body1" className={classes.stageLabel}>
                            {seminomaLabel}
                        </Typography>
                        <CalendarProgressMobile
                            dateOfDiagnosis={dateOfDiagnosis}
                            endOfTreatmentDate={endOfTreatementDate}
                            timelineProgressBarClass={classes.timelineProgressBar}
                            useDefaultColor={true}
                            noBottomMarginStartEndContainer={true}
                        />
                    </CardContent>
                </Card>
            </>
        );
    };

    SubPageContent = () => (
        <>
            {tutorialCurrentPage === 4 && <BloodTestCard />}
            {tutorialCurrentPage === 5 && <NextUp cardTitle={"Doctor's Note"} cardText={WAITING_REVIEW_NEXT_UP_CARD_TEXT} CardImage={NoteIcon} />}
            {tutorialCurrentPage === 6 && <JourneyCard />}
        </>
    );

    return (
        <Box className={classes.tutorialContent}>
            {!isMobile && tutorialCurrentPage === 3 && <img src={ChatModal} alt="Closed Chat Modal" className={classes.chatModal} />}
            {[4, 5, 6].includes(tutorialCurrentPage) && (
                <ContentLayout className={classes.subPageContent}>
                    <Box ref={customRef}>
                        <SubPageContent />
                    </Box>
                </ContentLayout>
            )}
            <TutorialFloatingGrid tutorialCurrentPage={tutorialCurrentPage} complexGridInfo={multiPageInfo} isMobile={isMobile} type={type} xCord={x} />
        </Box>
    );
}
